// 사진 그리기 데이터 검증·정규화
// 서버 jsonb는 키 순서를 바꿔 돌려주고(SQLite는 문자열로 줄 수도 있다) 깨진 값이 섞일 수 있으므로,
// 화면·저장에 쓰기 전에 반드시 parsePhotoDrawing을 거친다. 잘못된 값은 null(그림 없음)로 본다.
// 계획: plan/photo-brush/02-renderer-and-settings.md

import { PHOTO_DRAWING_VERSION, type PhotoDrawing, type PhotoStroke } from './types'

// 서버 상한(FIELD_TYPES.drawing.maxJsonLength = 512KB) 안에 여유 있게 들어오게 하는 한도
export const PHOTO_DRAWING_LIMITS = {
  // 획 수
  maxStrokes: 500,
  // 획당 점 수 (숫자 개수는 2배)
  maxPointsPerStroke: 2000,
  // 전체 점 수 — 점 하나가 JSON에서 약 14바이트("0.1234,0.5678,")라 25,000점 ≈ 350KB
  maxTotalPoints: 25000,
  // 직렬화 길이 상한 (서버 512KB보다 작게)
  maxJsonLength: 480 * 1024,
} as const

// 두께 비율 상한 (짧은 변 대비)
const MAX_WIDTH_RATIO = 0.2
// 좌표 허용 오차 (반올림 오차)
const COORD_EPSILON = 1e-6
// rgba(…)/rgb(…)/#hex만 색으로 인정
const COLOR_PATTERN =
  /^(rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(\d*\.?\d+)\s*)?\)|#[0-9a-fA-F]{3,8})$/

/**
 * 좌표를 소수 4자리로 반올림 (긴 변 4000px 기준 0.4px 오차, JSON 크기 절감)
 * @param value 정규화 좌표
 */
export function roundCoord(value: number): number {
  return Math.round(value * 10000) / 10000
}

/**
 * 획 하나를 검증해 정규 키 순서의 새 객체로 만든다
 * @param raw 검증할 값
 * @returns 정상 획, 깨졌으면 null
 */
function parseStroke(raw: unknown): PhotoStroke | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  if (obj.kind !== 'free' && obj.kind !== 'poly') return null
  if (!Array.isArray(obj.points)) return null
  const points = obj.points as unknown[]
  // 최소 2점, 짝수 개
  if (points.length < 4 || points.length % 2 !== 0) return null
  if (points.length > PHOTO_DRAWING_LIMITS.maxPointsPerStroke * 2) return null
  const nextPoints: number[] = new Array(points.length)
  for (let i = 0; i < points.length; i++) {
    const value = points[i]
    if (typeof value !== 'number' || !Number.isFinite(value)) return null
    if (value < -COORD_EPSILON || value > 1 + COORD_EPSILON) return null
    nextPoints[i] = Math.min(1, Math.max(0, value))
  }
  if (typeof obj.color !== 'string' || !COLOR_PATTERN.test(obj.color.trim())) return null
  const width = obj.width
  if (typeof width !== 'number' || !Number.isFinite(width)) return null
  if (width <= 0 || width > MAX_WIDTH_RATIO) return null
  // dash는 boolean이 정본, 0/1도 받아 준다
  const dash = obj.dash === true || obj.dash === 1
  if (obj.dash !== undefined && typeof obj.dash !== 'boolean' && obj.dash !== 0 && obj.dash !== 1) {
    return null
  }
  const stroke: PhotoStroke = {
    kind: obj.kind,
    points: nextPoints,
    color: obj.color.trim(),
    width,
    dash,
  }
  // 점선 위상은 점선이고 양수일 때만 남긴다 (잘못된 값은 버리고 획은 살린다 — 무늬만 처음부터 시작)
  const dashOffset = obj.dashOffset
  if (dash && typeof dashOffset === 'number' && Number.isFinite(dashOffset)) {
    const rounded = roundCoord(dashOffset)
    if (rounded > 0) stroke.dashOffset = rounded
  }
  return stroke
}

/**
 * 저장·수신한 drawing 값을 검증해 정규화된 PhotoDrawing으로 만든다
 * 문자열이면 JSON.parse, 키 순서는 무관, 깨진 획은 버리고 남는 게 없으면 null
 * 한도를 넘는 획은 앞에서부터 한도까지만 남긴다
 * @param raw DB·서버·스토어에서 읽은 값
 */
export function parsePhotoDrawing(raw: unknown): PhotoDrawing | null {
  let value: unknown = raw
  if (typeof value === 'string') {
    if (!value) return null
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const obj = value as Record<string, unknown>
  if (obj.v !== PHOTO_DRAWING_VERSION) return null
  if (!Array.isArray(obj.strokes)) return null

  const strokes: PhotoStroke[] = []
  let totalPoints = 0
  for (const rawStroke of obj.strokes) {
    if (strokes.length >= PHOTO_DRAWING_LIMITS.maxStrokes) break
    const stroke = parseStroke(rawStroke)
    if (!stroke) continue
    const pointCount = stroke.points.length / 2
    if (totalPoints + pointCount > PHOTO_DRAWING_LIMITS.maxTotalPoints) break
    totalPoints += pointCount
    strokes.push(stroke)
  }
  if (strokes.length === 0) return null
  return { v: PHOTO_DRAWING_VERSION, strokes }
}

/**
 * 획 배열을 저장용 drawing으로 만든다 (빈 배열이면 null — 빈 strokes 저장 금지)
 * @param strokes 획 목록
 */
export function toPhotoDrawing(strokes: PhotoStroke[]): PhotoDrawing | null {
  if (strokes.length === 0) return null
  return { v: PHOTO_DRAWING_VERSION, strokes }
}

/**
 * 키 순서와 무관한 정본 직렬화 (비교·해시용)
 * @param drawing 그림 (null 허용)
 */
export function serializePhotoDrawing(drawing: PhotoDrawing | null | undefined): string {
  const parsed = parsePhotoDrawing(drawing)
  if (!parsed) return 'null'
  // parsePhotoDrawing이 정본 키 순서로 새 객체를 만들므로 JSON.stringify가 곧 정본 직렬화다
  return JSON.stringify(parsed)
}

/**
 * 두 그림이 같은지 (서버 jsonb 키 순서·문자열 저장 여부와 무관)
 * @param a 그림 A
 * @param b 그림 B
 */
export function isSamePhotoDrawing(a: unknown, b: unknown): boolean {
  return (
    serializePhotoDrawing(a as PhotoDrawing | null) ===
    serializePhotoDrawing(b as PhotoDrawing | null)
  )
}

/**
 * 저장 한도 검사 — 커밋 전에 호출해 넘으면 그 조작을 거절한다
 * @param drawing 저장하려는 그림
 * @returns 한도 안이면 true
 */
export function isWithinPhotoDrawingLimits(drawing: PhotoDrawing | null): boolean {
  if (!drawing) return true
  if (drawing.strokes.length > PHOTO_DRAWING_LIMITS.maxStrokes) return false
  let totalPoints = 0
  for (const stroke of drawing.strokes) {
    const pointCount = stroke.points.length / 2
    if (pointCount > PHOTO_DRAWING_LIMITS.maxPointsPerStroke) return false
    totalPoints += pointCount
  }
  if (totalPoints > PHOTO_DRAWING_LIMITS.maxTotalPoints) return false
  return JSON.stringify(drawing).length <= PHOTO_DRAWING_LIMITS.maxJsonLength
}
